import { motion } from 'framer-motion';
import { 
  CheckCircle2, Clock, Target, RotateCcw, ArrowRight, 
  Home, Trophy, Star, MessageSquare, Sparkles, Loader2, Lightbulb, BookOpen
} from 'lucide-react';
import { StepRecord, UnitContext, ComprehensiveEvaluation } from '../types';
import { useSoundHandlers } from '../hooks/useSoundEffects';
import { useTheme } from '../contexts/ThemeContext';

interface PracticeSummaryProps {
  sentenceText: string;
  steps: StepRecord[];
  totalScore: number;
  startedAt: string;
  completedAt: string;
  unitContext: UnitContext | null;
  hasNextSentence: boolean;
  onNextSentence: () => void;
  onRetry: () => void;
  onGoHome: () => void;
  comprehensiveEvaluation: ComprehensiveEvaluation | null;
  loadingEvaluation: boolean;
}

const stepNames = ['过程识别', '对象识别', '建立连接', '句子拆解', '逐层翻译'];

export default function PracticeSummary({
  sentenceText,
  steps,
  totalScore,
  startedAt: _startedAt,
  completedAt: _completedAt,
  unitContext,
  hasNextSentence,
  onNextSentence,
  onRetry,
  onGoHome,
  comprehensiveEvaluation,
  loadingEvaluation,
}: PracticeSummaryProps) {
  const { playClick, playHover } = useSoundHandlers();
  const { theme } = useTheme();
  const isQinghua = theme === 'qinghua';

  // 计算总用时
  const totalDuration = steps.reduce((sum, step) => sum + (step.duration_seconds || 0), 0);
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}分${secs}秒` : `${secs}秒`;
  };

  // 获取等级评价
  const getGrade = (score: number) => {
    if (score >= 0.9) return { text: '优秀', color: '#22c55e' };
    if (score >= 0.7) return { text: '良好', color: '#3b82f6' };
    if (score >= 0.6) return { text: '及格', color: '#f59e0b' };
    return { text: '需加强', color: '#ef4444' };
  };

  const grade = getGrade(totalScore);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-4xl mx-auto"
    >
      {/* 成绩概览卡片 */}
      <div 
        className="cyber-card p-8 mb-6 text-center"
        style={{
          background: isQinghua 
            ? 'linear-gradient(135deg, rgba(255, 253, 248, 0.98) 0%, rgba(248, 244, 232, 0.95) 100%)'
            : undefined
        }}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4"
          style={{
            background: `linear-gradient(135deg, ${grade.color}20, ${grade.color}10)`,
            border: `3px solid ${grade.color}`
          }}
        >
          <Trophy className="w-12 h-12" style={{ color: grade.color }} />
        </motion.div>

        <h2 
          className="text-3xl font-display mb-2"
          style={{ color: grade.color }}
        >
          {grade.text}
        </h2>
        
        <div 
          className="text-5xl font-display font-bold mb-4"
          style={{ color: 'var(--theme-accent)' }}
        >
          {Math.round(totalScore * 100)}%
        </div>

        <div className="flex items-center justify-center gap-6 text-sm" style={{ color: 'var(--theme-text-muted)' }}>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>用时 {formatDuration(totalDuration)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            <span>完成 5/5 步</span>
          </div>
        </div>

        {unitContext && (
          <div 
            className="mt-4 text-sm"
            style={{ color: 'var(--theme-text-secondary)' }}
          >
            {unitContext.courseName} · {unitContext.unitName} · 第 {unitContext.currentSentenceOrder} 句
          </div>
        )}
      </div>

      {/* 句子原文 */}
      <div className="cyber-card p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--theme-accent)' }} />
          <span className="text-xs font-body uppercase tracking-wider" style={{ color: 'var(--theme-text-muted)' }}>
            练习句子
          </span>
        </div>
        <p 
          className="font-mono text-sm leading-relaxed"
          style={{ color: 'var(--theme-text-primary)' }}
        >
          {sentenceText}
        </p>
      </div>

      {/* 各步骤详情 */}
      <div className="cyber-card p-6 mb-6">
        <h3 
          className="font-display text-lg mb-4"
          style={{ color: 'var(--theme-accent)' }}
        >
          各步骤成绩
        </h3>

        <div className="space-y-3">
          {steps.map((step, index) => {
            const stepScore = step.score || 0;
            const stepGrade = getGrade(stepScore);
            
            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="flex items-center gap-4 p-4 rounded-lg"
                style={{
                  background: 'var(--theme-glow-color)',
                  border: '1px solid var(--theme-border)'
                }}
              >
                {/* 步骤编号 */}
                <div 
                  className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-display font-bold"
                  style={{
                    background: `${stepGrade.color}20`,
                    color: stepGrade.color
                  }}
                >
                  {step.step_num}
                </div>

                {/* 步骤名称 */}
                <div className="flex-1">
                  <div className="font-display" style={{ color: 'var(--theme-text-primary)' }}>
                    {stepNames[step.step_num - 1]}
                  </div>
                  <div className="text-xs flex items-center gap-3" style={{ color: 'var(--theme-text-muted)' }}>
                    <span>尝试 {step.attempts} 次</span>
                    {step.duration_seconds && (
                      <span>用时 {formatDuration(step.duration_seconds)}</span>
                    )}
                  </div>
                </div>

                {/* 分数 */}
                <div className="text-right">
                  <div 
                    className="font-display text-xl font-bold"
                    style={{ color: stepGrade.color }}
                  >
                    {Math.round(stepScore * 100)}%
                  </div>
                </div>

                {/* 完成图标 */}
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </motion.div>
            );
          })}
        </div>

      </div>

      {/* AI 综合评价 */}
      <div className="cyber-card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5" style={{ color: 'var(--theme-accent)' }} />
          <h3 
            className="font-display text-lg"
            style={{ color: 'var(--theme-accent)' }}
          >
            AI 综合评价
          </h3>
        </div>

        {loadingEvaluation ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
            <span className="ml-2" style={{ color: 'var(--theme-text-muted)' }}>正在生成综合评价...</span>
          </div>
        ) : comprehensiveEvaluation ? (
          <div className="space-y-4">
            {/* 总体分析 */}
            <div 
              className="p-4 rounded-lg"
              style={{
                background: isQinghua ? 'rgba(46, 90, 168, 0.05)' : 'rgba(168, 85, 247, 0.1)',
                border: `1px solid ${isQinghua ? 'rgba(46, 90, 168, 0.2)' : 'rgba(168, 85, 247, 0.3)'}`
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4" style={{ color: 'var(--theme-accent)' }} />
                <span className="text-sm font-display" style={{ color: 'var(--theme-accent)' }}>
                  总体分析
                </span>
              </div>
              <p 
                className="text-sm leading-relaxed"
                style={{ color: 'var(--theme-text-primary)' }}
              >
                {comprehensiveEvaluation.overallAnalysis}
              </p>
            </div>

            {/* 各步骤点评 */}
            <div 
              className="p-4 rounded-lg"
              style={{
                background: isQinghua ? 'rgba(46, 90, 168, 0.03)' : 'rgba(59, 130, 246, 0.08)',
                border: `1px solid ${isQinghua ? 'rgba(46, 90, 168, 0.15)' : 'rgba(59, 130, 246, 0.2)'}`
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--theme-accent)' }} />
                <span className="text-sm font-display" style={{ color: 'var(--theme-accent)' }}>
                  各步骤点评
                </span>
              </div>
              <div className="space-y-2">
                {comprehensiveEvaluation.stepHighlights.map((highlight, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-2 text-sm"
                    style={{ color: 'var(--theme-text-secondary)' }}
                  >
                    <span 
                      className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{
                        background: `${getGrade(steps[index]?.score || 0).color}20`,
                        color: getGrade(steps[index]?.score || 0).color
                      }}
                    >
                      {index + 1}
                    </span>
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 改进建议 */}
            {comprehensiveEvaluation.improvements.length > 0 && (
              <div 
                className="p-4 rounded-lg"
                style={{
                  background: isQinghua ? 'rgba(234, 179, 8, 0.05)' : 'rgba(234, 179, 8, 0.1)',
                  border: `1px solid ${isQinghua ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.3)'}`
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-display text-yellow-500">
                    改进建议
                  </span>
                </div>
                <ul className="space-y-1">
                  {comprehensiveEvaluation.improvements.map((improvement, index) => (
                    <li 
                      key={index}
                      className="text-sm flex items-start gap-2"
                      style={{ color: 'var(--theme-text-secondary)' }}
                    >
                      <span className="text-yellow-500">•</span>
                      <span>{improvement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 学习重点 */}
            <div 
              className="p-4 rounded-lg"
              style={{
                background: isQinghua ? 'rgba(34, 197, 94, 0.05)' : 'rgba(34, 197, 94, 0.1)',
                border: `1px solid ${isQinghua ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.3)'}`
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-green-500" />
                <span className="text-sm font-display text-green-500">
                  学习重点
                </span>
              </div>
              <p 
                className="text-sm"
                style={{ color: 'var(--theme-text-secondary)' }}
              >
                {comprehensiveEvaluation.studyFocus}
              </p>
            </div>
          </div>
        ) : (
          <div 
            className="text-center py-6"
            style={{ color: 'var(--theme-text-muted)' }}
          >
            <p>AI 综合评价暂不可用</p>
          </div>
        )}
      </div>

      {/* 操作按钮 */}
      <div className="flex items-center justify-center gap-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => { playClick(); onGoHome(); }}
          onMouseEnter={playHover}
          className="cyber-button flex items-center gap-2"
        >
          <Home className="w-5 h-5" />
          <span className="font-display">返回首页</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => { playClick(); onRetry(); }}
          onMouseEnter={playHover}
          className="cyber-button flex items-center gap-2"
        >
          <RotateCcw className="w-5 h-5" />
          <span className="font-display">重新练习</span>
        </motion.button>

        {unitContext && hasNextSentence && (
          <motion.button
            whileHover={{ scale: 1.02, x: 3 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => { playClick(); onNextSentence(); }}
            onMouseEnter={playHover}
            className="cyber-button-primary flex items-center gap-2"
          >
            <span className="font-display">下一句</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        )}

        {unitContext && !hasNextSentence && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg"
            style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)'
            }}
          >
            <Star className="w-5 h-5 text-green-400" />
            <span className="text-green-400 font-display">单元已完成！</span>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

